import { ChevronUp, ChevronDown, Loader2, Eye, UserCheck, UserX } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';

export interface TableCustomer {
  id: string;
  joinDate: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  totalSpent: number;
  totalOrders: number;
  lastOrder: string;
  membershipTier: string;
  isMember: boolean;
  isActive: boolean;
  customerType: string;
  customerNumber: string | null;
  subscriptionFrequency: string | null;
  subscriptionEndDate: string | null;
}

interface CustomersTableProps {
  customers: TableCustomer[];
  page: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  onPageChange: (newQuery: { page?: number; limit?: number }) => void;
  loading?: boolean;
  filterField?: string;
  filterValue?: string;
  customerType?: 'all' | 'general' | 'members';
  onViewDetails: (customerId: string) => void;
  onToggleActive: (customerId: string, currentIsActive: boolean) => void;
}

const TIER_COLORS: Record<string, string> = {
  BRONZE: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
  SILVER: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  GOLD: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
  PLATINUM: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400',
};

export function CustomersTable({
  customers,
  page,
  totalPages,
  totalCount,
  limit,
  onPageChange,
  loading = false,
  filterField = '',
  filterValue = '',
  customerType = 'all',
  onViewDetails,
  onToggleActive,
}: CustomersTableProps) {
  const [itemsPerPage, setItemsPerPage] = useState(limit);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [perPageDropdownOpen, setPerPageDropdownOpen] = useState(false);
  const { formatCurrency } = useCurrency();

  useEffect(() => {
    setItemsPerPage(limit);
  }, [limit]);

  // Filter by customer type
  let typeFiltered = customers;
  if (customerType === 'general') {
    typeFiltered = customers.filter((c) => !c.isMember);
  } else if (customerType === 'members') {
    typeFiltered = customers.filter((c) => c.isMember);
  }

  // Filter by field/value
  const filteredCustomers = typeFiltered.filter((customer) => {
    if (!filterField || !filterValue) return true;
    const sv = filterValue.toLowerCase();
    switch (filterField) {
      case 'id':    return customer.id.toLowerCase().includes(sv);
      case 'name':  return customer.name.toLowerCase().includes(sv);
      case 'location': return customer.location.toLowerCase().includes(sv);
      case 'totalSpent': return customer.totalSpent.toString().includes(sv);
      case 'lastOrder':  return customer.lastOrder.toLowerCase().includes(sv);
      default: return true;
    }
  });

  // Sort
  const sorted = [...filteredCustomers].sort((a, b) => {
    if (!sortColumn) return 0;
    let aVal: any, bVal: any;
    switch (sortColumn) {
      case 'id':        aVal = a.id; bVal = b.id; break;
      case 'joinDate':  aVal = new Date(a.joinDate).getTime(); bVal = new Date(b.joinDate).getTime(); break;
      case 'name':      aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
      case 'location':   aVal = a.location.toLowerCase(); bVal = b.location.toLowerCase(); break;
      case 'customerType': aVal = a.customerType.toLowerCase(); bVal = b.customerType.toLowerCase(); break;
      case 'totalSpent': aVal = a.totalSpent; bVal = b.totalSpent; break;
      default: return 0;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Use server-side pagination - sorted data is already current page from server
  const paginated = sorted;

  const handleSort = (col: string) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const startIndex = totalCount === 0 ? 0 : (page - 1) * limit;
  const endIndex = Math.min(startIndex + customers.length, totalCount);

  const goToPage = (pageNum: number) => {
    if (pageNum >= 1 && pageNum <= totalPages) onPageChange({ page: pageNum });
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setPerPageDropdownOpen(false);
    onPageChange({ page: 1, limit: newLimit });
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    }
    return pages;
  };

  const SortIcon = ({ col }: { col: string }) => (
    <div className="flex flex-col">
      <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === col && sortDirection === 'asc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
      <ChevronDown className={`w-3 h-3 ${sortColumn === col && sortDirection === 'desc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading customers…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-800">
              {[
                { key: 'customerNumber', label: 'Customer No' },
                { key: 'name', label: 'Customer Name' },
                { key: 'customerType', label: 'Customer Type' },
                { key: 'status', label: 'Status' },
                { key: 'joinDate', label: 'Join Date' },
                { key: 'lastOrder', label: 'Last Order' },
                { key: 'totalSpent', label: 'Total Spent' },
              ].map(({ key, label }) => (
                <th key={key} className="text-left px-6 py-3">
                  <button
                    onClick={() => handleSort(key)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                  >
                    {label}
                    <SortIcon col={key} />
                  </button>
                </th>
              ))}
              <th className="text-left px-6 py-3">
                <span className="text-xs font-semibold text-gray-900 dark:text-white">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                  No customers found
                </td>
              </tr>
            ) : (
              paginated.map((customer) => (
                <tr
                  key={customer.id}
                  className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  {/* Customer Number */}
                  <td className="px-6 py-3">
                    {customer.customerNumber ? (
                      <button
                        onClick={() => onViewDetails(customer.id)}
                        className="inline-flex items-center px-2 py-0.5 rounded-md font-mono text-xs font-semibold bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                      >
                        {customer.customerNumber}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                    )}
                  </td>
                  {/* Customer Name */}
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">
                          {customer.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-gray-900 dark:text-white">{customer.name}</span>
                    </div>
                  </td>
                  {/* Customer Type */}
                  <td className="px-6 py-3">
                    {customer.customerType ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400">
                        {customer.customerType}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                    )}
                  </td>
                  {/* Status */}
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      customer.isActive
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    }`}>
                      {customer.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {/* Join Date */}
                  <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400">{customer.joinDate}</td>
                  {/* Last Order */}
                  <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400">{customer.lastOrder}</td>
                  {/* Total Spent */}
                  <td className="px-6 py-3 text-xs font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(parseFloat(customer.totalSpent as any))}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        title="View details"
                        onClick={() => onViewDetails(customer.id)}
                        className="p-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors group"
                      >
                        <Eye className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400" />
                      </button>
                      <button
                        title={customer.isActive ? 'Deactivate' : 'Activate'}
                        onClick={() => onToggleActive(customer.id, customer.isActive)}
                        className={`p-1.5 rounded-lg transition-colors group ${
                          customer.isActive
                            ? 'hover:bg-orange-50 dark:hover:bg-orange-900/20'
                            : 'hover:bg-green-50 dark:hover:bg-green-900/20'
                        }`}
                      >
                        {customer.isActive
                          ? <UserX className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 group-hover:text-orange-500 dark:group-hover:text-orange-400" />
                          : <UserCheck className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 group-hover:text-green-600 dark:group-hover:text-green-400" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {totalCount === 0 ? 'No entries' : `Showing ${startIndex + 1}–${endIndex} of ${totalCount}`}
          </div>
          <div className="relative">
            <button
              onClick={() => setPerPageDropdownOpen(!perPageDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {itemsPerPage} per page <ChevronDown className="w-3 h-3" />
            </button>
            {perPageDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setPerPageDropdownOpen(false)} />
                <div className="absolute left-0 bottom-full mb-2 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                  {[10, 25, 50, 100].map((v) => (
                    <button
                      key={v}
                      onClick={() => handleItemsPerPageChange(v)}
                      className={`w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${itemsPerPage === v ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-200'}`}
                    >
                      {v} per page
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
          >
            <ChevronUp className="w-3 h-3 -rotate-90" /> Previous
          </button>
          {getPageNumbers().map((pageNum, idx) => (
            <button
              key={idx}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${typeof pageNum === 'number' && page === pageNum ? 'text-white bg-indigo-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              onClick={() => typeof pageNum === 'number' && goToPage(pageNum)}
            >
              {pageNum}
            </button>
          ))}
          <button
            className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages || totalPages === 0}
          >
            Next <ChevronUp className="w-3 h-3 rotate-90" />
          </button>
        </div>
      </div>
    </div>
  );
}
